import * as services from '../Services';

import { SUBSCRIPTION_MESSAGES, TAB_FORM_LIST } from '../utils/consts';

import Access from 'ui/Access';
import React from 'react';
import UsersView from '../../shared/users/UsersView';
import { flowRight } from 'lodash/fp';
import { userActions } from '../../shared/users/types';
import { withAppContext } from '../../AppContext';
import { withSubscriptionContext } from '../SubscriptionContext';

class Users extends React.Component {
    state = {
        users: [],
        usersStats: {},
        usersShowActionResult: false,
        userLoading: true,
        currentUser: null,
        currentGroup: null,
        groups: [],
        loggedInUser: null,
    };

    componentDidMount() {
        const { invitations, users } = this.props.subscription.data;
        const subscription_users = users || [];
        this.setState({
            users: subscription_users.concat(invitations || []),
            usersStats: this.getStats(this.props.subscription.data),
            userLoading: false,
            loggedInUser: this.props.app.currentUser,
        });
    }

    showMessage = (msg, delay = null) => {
        let options = {
            variant: 'info',
        };
        if (delay) {
            options['autoHideDuration'] = delay;
        }
        this.props.onMessage(msg, options);
    };

    handleFormCancel = () => {
        this.setState({
            formErrors: null,
            currentUser: null,
            currentGroup: null,
        });
    };

    getStats(subscription) {
        const { seats, remaining_seats, invitations_count } = subscription;
        return {
            account_count: seats - remaining_seats,
            pending_approval_count: invitations_count,
            remaining_seats: remaining_seats,
        };
    }

    getActions = () => {
        const isExternalActiveUser = user =>
            user.managed_by !== 'PB' && user.status === 'active';
        const canBeDisabled = user =>
            user.uuid !== this.state.loggedInUser.uuid;
        return [
            {
                key: userActions.CONVERT_TO_PERSONAL,
                name: 'Convert to personal account',
                notAllowedUsersStatuses: [
                    'disabled',
                    'invited',
                    'unmanaged',
                    'pending_approval',
                    'Active Directory',
                ],
            },
            {
                key: userActions.DELETE_USERS,
                name: 'Delete users',
                notAllowedUsersStatuses: ['unmanaged', 'pending_approval'],
                actionFilter(user) {
                    return (
                        !isExternalActiveUser(user) && !user.is_primary_account
                    );
                },
            },
            {
                key: userActions.DISABLE_USERS,
                name: 'Disable users',
                notAllowedUsersStatuses: [
                    'disabled',
                    'invited',
                    'unmanaged',
                    'pending_approval',
                ],
                actionFilter(user) {
                    return canBeDisabled(user);
                },
            },
            {
                key: userActions.ENABLE_USERS,
                name: 'Enable users',
                notAllowedUsersStatuses: [
                    'active',
                    'invited',
                    'unmanaged',
                    'pending_approval',
                ],
            },
            {
                key: userActions.REQUIRE_PASSWORD_CHANGE,
                name: 'Require password change',
                notAllowedUsersStatuses: [
                    'disabled',
                    'invited',
                    'unmanaged',
                    'pending_approval',
                ],
            },
            {
                key: userActions.RESET_PASSWORD,
                name: 'Reset master password',
                notAllowedUsersStatuses: [
                    'disabled',
                    'invited',
                    'unmanaged',
                    'pending_approval',
                ],
            },
        ];
    };

    getError = response => {
        let errorMessage = response.error.split(':');
        if (errorMessage.length === 2) {
            errorMessage = errorMessage[1].trim();
        } else {
            errorMessage = errorMessage[0];
        }
        return errorMessage;
    };

    fetchUsers(usersShowActionResult = false) {
        services
            .loadSubscription(this.props.id)
            .then(json => {
                const [subscription] = json;
                const users = subscription.users || [];
                this.setState({
                    users: users.concat(subscription.invitations || []),
                    usersStats: this.getStats(subscription),
                    userLoading: false,
                    usersShowActionResult,
                });
            })
            .catch(() => {
                this.setState({
                    userLoading: false,
                });
            });
    }

    handleFetchUser = ({ uuid, item_type }) => {
        const data = {
            [`${item_type}_uuid`]: uuid,
        };
        services.loadUser(data).then(json => {
            if (json.error) {
                this.setState({ formErrors: json.error });
                return;
            }
            this.setState({
                currentUser: json,
            });
        });
    };

    addUsers = body => {
        return services
            .addUsers(body)
            .then(response => {
                if (!response || response.error || !Array.isArray(response)) {
                    throw new Error(response?.error);
                }
                this.showMessage(SUBSCRIPTION_MESSAGES.Create);
                this.showMigrationAccountMessage(response);
                this.setState({ userLoading: true, formErrors: null });
                this.fetchUsers();
            })
            .catch(error => {
                const message = error?.message || SUBSCRIPTION_MESSAGES.Unknown;
                this.setState({ formErrors: message });
            });
    };

    showMigrationAccountMessage = invitations => {
        const migratingAccountsEmails = invitations
            .filter(inv => Boolean(inv.accepting_account))
            .map(inv => inv.email)
            .join(', ');

        if (migratingAccountsEmails) {
            const msg = `${SUBSCRIPTION_MESSAGES.Migrate} ${migratingAccountsEmails}`;
            this.showMessage(msg, 5000);
        }
    };

    handleAddUser = usersData => {
        const body = {
            email: usersData.email,
            subscription: this.props.id,
            origin: 'Portal Invite',
            options: {
                first_name: usersData.first_name,
                last_name: usersData.last_name,
            },
        };
        this.addUsers(body);
    };

    handleAddUsers = usersData => {
        const body = usersData.map(user => {
            return {
                email: user.email,
                subscription: this.props.id,
                origin: 'Portal Invite',
                options: {
                    first_name: user.first_name,
                    last_name: user.last_name,
                },
            };
        });
        this.addUsers(body);
    };

    handleUpdate = (data, message) => {
        const { app } = this.props;
        const shouldUpdateUserData = app.currentUser.uuid === data.uuid;
        return services.updateUsers(data).then(json => {
            if (json.error) {
                this.setState({
                    formErrors: this.getError(json),
                });
            } else {
                this.showMessage(message);
                this.setState({ userLoading: true, currentUser: null }, () => {
                    this.fetchUsers();
                    if (shouldUpdateUserData) {
                        app.updateUserData();
                    }
                });
            }
        });
    };

    handleUpdateUser = userData => {
        const data = {
            accounts: [userData],
        };
        this.handleUpdate(data, SUBSCRIPTION_MESSAGES.Update);
    };

    handleDeleteUsers = selectedUsers => {
        const data = selectedUsers.reduce(
            (acc, { uuid, item_type }) => {
                if (item_type === 'account') {
                    acc.accounts.push({ uuid });
                } else {
                    acc.invitations.push({ uuid });
                }
                return acc;
            },
            { accounts: [], invitations: [] }
        );
        this.setState({ userLoading: true }, () => {
            this.showMessage(SUBSCRIPTION_MESSAGES.Delete);
            services.removeUsers(data).then(json => {
                if (json.error) {
                    this.setState({
                        formErrors: this.getError(json),
                    });
                } else {
                    this.fetchUsers();
                }
            });
        });
    };

    handleDisableUsers = selectedUsers => {
        const data = {
            accounts: selectedUsers.map(({ uuid }) => ({
                uuid,
                disabled: true,
            })),
        };
        this.handleUpdate(data, SUBSCRIPTION_MESSAGES.Disable);
    };

    handleEnabledUsers = selectedUsers => {
        const data = {
            accounts: selectedUsers.map(({ uuid }) => ({
                uuid,
                enabled: true,
            })),
        };
        this.handleUpdate(data, SUBSCRIPTION_MESSAGES.Enable);
    };

    handleRequireUsersPasswordChange = selectedUsers => {
        const data = {
            accounts: selectedUsers.map(({ uuid }) => ({
                uuid,
                master_password_change_required: true,
            })),
        };
        this.handleUpdate(data, SUBSCRIPTION_MESSAGES.ChangePassword);
    };

    handleConvertToPersonalAccount = selectedUsers => {
        this.setState({ userLoading: true }, () => {
            this.showMessage(SUBSCRIPTION_MESSAGES.Convert);
            services.convertToPersonal(selectedUsers).then(json => {
                if (json.error) {
                    this.setState({
                        formErrors: this.getError(json),
                    });
                } else {
                    this.fetchUsers();
                }
            });
        });
    };

    handleResetUsersPassword = (
        selectedUsers,
        showActionResult,
        option = {}
    ) => {
        this.setState({ userLoading: true, currentUser: null }, () => {
            this.showMessage(SUBSCRIPTION_MESSAGES.ResetPassword);
            const data = {
                accounts: selectedUsers.map(({ uuid }) => ({
                    uuid,
                })),
                ...option,
            };
            services.resetPasswordUser(data);
        });
        this.fetchUsers(showActionResult);
    };

    handleResetForm = () => {
        this.setState({ formErrors: null });
    };

    validDeleteUsers(users) {
        const { currentUser } = this.app;
        if (currentUser && users.some(user => user.uuid === currentUser.uuid)) {
            return userActions.DELETE_YOURSELF_INVALID;
        }
        return userActions.DELETE_USERS;
    }

    render() {
        const { usersStats } = this.state;
        const actions = this.getActions();
        const stats = [
            { label: 'Active Users', value: usersStats.account_count },
            { label: 'Available Licenses', value: usersStats.remaining_seats },
            {
                label: 'Pending Approval',
                value: usersStats.pending_approval_count,
            },
        ];
        const permissions = {
            users: Access.SUBSCRIPTION_USERS,
            groups: Access.SUBSCRIPTION_GROUPS,
        };

        return (
            <UsersView
                users={this.state.users}
                stats={stats}
                groups={this.state.groups}
                userLoading={this.state.userLoading}
                currentUser={this.state.currentUser}
                formErrors={this.state.formErrors}
                onReset={this.handleResetForm}
                showActionResult={this.state.usersShowActionResult}
                onActionResult={() =>
                    this.setState({
                        usersShowActionResult: false,
                    })
                }
                onAddUser={this.handleAddUser}
                onAddUsers={this.handleAddUsers}
                onUpdateUser={this.handleUpdateUser}
                onDeleteUsers={this.handleDeleteUsers}
                onDisableUsers={this.handleDisableUsers}
                onEnableUsers={this.handleEnabledUsers}
                onConvertToPersonalAccount={this.handleConvertToPersonalAccount}
                onResetUsersPassword={this.handleResetUsersPassword}
                onFormCancel={this.handleFormCancel}
                onFetchUser={this.handleFetchUser}
                onRequireUsersPasswordChange={
                    this.handleRequireUsersPasswordChange
                }
                editable={this.props.editable}
                actions={actions}
                permissions={permissions}
                tabs={TAB_FORM_LIST}
                validDeleteUsers={this.validDeleteUsers}
                loggedInUser={this.props.app.currentUser}
            />
        );
    }
}

export default flowRight(withAppContext, withSubscriptionContext)(Users);
